import React, { useCallback, useId, useRef } from 'react';
import type { ParseKeys } from 'i18next';
import { useTranslation } from 'react-i18next';
import { AlertCircle, AlertTriangle, Check, CheckCircle2, Info } from 'lucide-react';

import { Checkbox, IconWrapper, Tag, Typography } from '@/components/ui/atoms';
import { IllustratedEmptyState, Loader } from '@/components/ui/molecules';
import { cn } from '@/utils/cn';
import { JobEditStrategy, JobRescoreSummary } from '../../../types/job.types';

type JobKey = ParseKeys<'job'>;

const STRATEGIES: readonly JobEditStrategy[] = [
  'update_job',
  'update_job_exam_and_rescore',
];

const CARDS: ReadonlyArray<{
  id: JobEditStrategy;
  title: JobKey;
  body: JobKey;
  badge?: boolean;
  bullets?: readonly JobKey[];
  showCredit?: boolean;
}> = [
  {
    id: 'update_job',
    title: 'edit_job_modal.strategy_update_job_title',
    body: 'edit_job_modal.strategy_update_job_body',
    badge: true,
  },
  {
    id: 'update_job_exam_and_rescore',
    title: 'edit_job_modal.strategy_update_job_exam_and_rescore_title',
    body: 'edit_job_modal.strategy_update_job_exam_and_rescore_body',
    bullets: [
      'edit_job_modal.strategy_regen_bullet_exam',
      'edit_job_modal.strategy_regen_bullet_rescore',
      'edit_job_modal.strategy_regen_bullet_quota',
    ],
    showCredit: true,
  },
];

export interface EditJobReviewPhaseProps {
  hasChanges: boolean;
  isImpactPending: boolean;
  isImpactError: boolean;
  impact: JobRescoreSummary | null;
  editStrategy: JobEditStrategy;
  onStrategyChange: (strategy: JobEditStrategy) => void;
  isSaving: boolean;
}

const pad = 'flex flex-col items-center justify-center text-center py-10 px-2';

/** Validate-step body for Edit Job (impact states + strategy cards). */
export const EditJobReviewPhase: React.FC<EditJobReviewPhaseProps> = ({
  hasChanges,
  isImpactPending,
  isImpactError,
  impact,
  editStrategy,
  onStrategyChange,
  isSaving,
}) => {
  const { t } = useTranslation('job');
  const groupId = useId();
  const refs = useRef<Partial<Record<JobEditStrategy, HTMLDivElement | null>>>({});

  const select = useCallback(
    (id: JobEditStrategy, focus = false) => {
      onStrategyChange(id);
      if (focus) requestAnimationFrame(() => refs.current[id]?.focus());
    },
    [onStrategyChange]
  );

  const onKeyDown = (e: React.KeyboardEvent, id: JobEditStrategy) => {
    const i = STRATEGIES.indexOf(id);
    if (i < 0) return;
    const next =
      e.key === 'ArrowRight' || e.key === 'ArrowDown'
        ? (i + 1) % STRATEGIES.length
        : e.key === 'ArrowLeft' || e.key === 'ArrowUp'
          ? (i - 1 + STRATEGIES.length) % STRATEGIES.length
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? STRATEGIES.length - 1
              : -1;
    if (next < 0) return;
    e.preventDefault();
    select(STRATEGIES[next], true);
  };

  if (!hasChanges) {
    return (
      <div className={pad}>
        <IllustratedEmptyState
          icon={<Info />}
          title={t('edit_job_modal.no_changes_title')}
          description={t('edit_job_modal.no_changes_body')}
        />
      </div>
    );
  }
  if (isImpactPending || (!impact && !isImpactError)) {
    return (
      <div className={pad}>
        <Loader title={t('edit_job_modal.checking_impact')} message="" compact />
      </div>
    );
  }
  if (!impact) {
    return (
      <div className={cn(pad, 'gap-3')}>
        <IconWrapper size="lg" color="purple">
          <AlertCircle />
        </IconWrapper>
        <Typography variant="h6" weight="semibold" color="primary">
          {t('edit_job_modal.impact_unavailable')}
        </Typography>
      </div>
    );
  }
  if (!impact.scoring_changed) {
    return (
      <div className={pad}>
        <IllustratedEmptyState
          icon={<CheckCircle2 />}
          title={t('edit_job_modal.no_rescore_title')}
          description={t('edit_job_modal.no_rescore_body')}
        />
      </div>
    );
  }

  const credit = t('edit_job_modal.strategy_credit_impact', {
    count: impact.candidates_to_rescore ?? 0,
  });

  return (
    <div className="flex flex-col gap-3">
      <Typography id={groupId} as="p" variant="body-sm" weight="semibold" color="primary">
        {t('edit_job_modal.strategy_prompt')}
      </Typography>
      <div className="grid grid-cols-1 gap-3" role="radiogroup" aria-labelledby={groupId}>
        {CARDS.map((card) => {
          const selected = editStrategy === card.id;
          const title = t(card.title);
          return (
            <div
              key={card.id}
              role="radio"
              aria-checked={selected}
              tabIndex={isSaving ? -1 : selected ? 0 : -1}
              ref={(el) => {
                refs.current[card.id] = el;
              }}
              onClick={() => !isSaving && select(card.id)}
              onKeyDown={(e) => onKeyDown(e, card.id)}
              className={cn(
                'w-full text-left rounded-lg border px-3.5 py-3 cursor-pointer transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple/40 focus-visible:ring-offset-1',
                selected
                  ? 'border-purple/40 bg-purple/[0.03]'
                  : 'border-gray-200 bg-white hover:border-gray-300',
                isSaving && 'opacity-50 cursor-not-allowed'
              )}
            >
              <div className="flex flex-col gap-3 h-full">
                <div className="flex items-start gap-2.5">
                  <Checkbox
                    shape="circle"
                    size="md"
                    checked={selected}
                    disabled={isSaving}
                    tabIndex={-1}
                    aria-hidden
                    aria-label={title}
                    className="pointer-events-none mt-0.5"
                    onChange={() => undefined}
                  />
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <Typography as="span" variant="body-md" weight="semibold" color="primary">
                        {title}
                      </Typography>
                      {card.badge && (
                        <Tag intent="secondary" variant="subtle" size="sm" palette="brand">
                          {t('edit_job_modal.strategy_default_badge')}
                        </Tag>
                      )}
                    </div>
                    <Typography as="p" variant="body-sm" color="secondary" className="leading-snug">
                      {t(card.body)}
                    </Typography>
                    {card.bullets?.map((key) => (
                      <div key={key} className="mt-auto inline-flex items-center gap-2">
                        <IconWrapper size="sm" color="purple"><Check /></IconWrapper>
                        <Typography as="span" variant="body-sm" color="secondary">
                          {t(key)}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </div>
                {card.showCredit && (
                  <p
                    role="status"
                    className="mt-auto inline-flex items-center gap-1 border-t border-light-lila pt-3"
                  >
                    <IconWrapper size="sm" color="secondary">
                      <AlertTriangle />
                    </IconWrapper>
                    <Typography as="span" variant="body-sm" color="muted" className="leading-none">
                      {credit}
                    </Typography>
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
